import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Divider,
    IconButton,
    Tab,
    Tabs,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import AddLinkIcon from '@mui/icons-material/AddLink';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { toast } from 'react-toastify';
import { useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';
import DataService from '../../DataService';
import ItemTagManager from '../../components/ItemTagManager';

// ─── Design tokens ────────────────────────────────────────────────────────────

const ACCENT_ATTENTION  = '#f59e0b';
const ACCENT_ACTIVATION = '#6366f1';
const IMG_BG            = '#0d0f12';
const SURFACE           = '#ffffff';
const CARD_SHADOW       = '0 2px 12px rgba(0,0,0,.07), 0 1px 3px rgba(0,0,0,.05)';
const CARD_RADIUS       = 2;
const IMAGE_CONTROL_BUTTON_SX = {
    width: 34,
    height: 34,
    bgcolor: 'rgba(0,0,0,.62)',
    color: '#f9fafb',
    border: '1px solid rgba(255,255,255,.16)',
    boxShadow: '0 6px 18px rgba(0,0,0,.26)',
    '&:hover': {
        bgcolor: 'rgba(0,0,0,.82)',
        color: '#fff',
    },
};
const IMAGE_NAV_BUTTON_SX = {
    ...IMAGE_CONTROL_BUTTON_SX,
    width: 40,
    height: 40,
};
const LOGO_CHECKERBOARD_SX = {
    backgroundColor: '#f9fafb',
    backgroundImage: `
        linear-gradient(45deg, #d1d5db 25%, transparent 25%),
        linear-gradient(-45deg, #d1d5db 25%, transparent 25%),
        linear-gradient(45deg, transparent 75%, #d1d5db 75%),
        linear-gradient(-45deg, transparent 75%, #d1d5db 75%)
    `,
    backgroundSize: '10px 10px',
    backgroundPosition: '0 0, 0 5px, 5px -5px, -5px 0',
};

function getBrandOptionLabel(option) {
    if (typeof option === 'string') return option;
    if (!option) return '';
    return option.name ?? option.brandName ?? option.displayName ?? option.meta?.['.name'] ?? '';
}

function getBrandOptionLogoUrl(option) {
    if (!option || typeof option === 'string') return '';
    const firstLogoVersion = option.logoVersions?.[0] ?? option.logoVersionArray?.[0];
    return firstLogoVersion?.iconUrl ?? firstLogoVersion?.thumbUrl ?? firstLogoVersion?.rasterUrl ?? option.iconUrl ?? '';
}

function getBrandOptionId(option) {
    if (!option || typeof option === 'string') return null;
    return option.brandId ?? option.id ?? null;
}

function getBrandMatchReason(option) {
    if (!option || typeof option === 'string' || !option.matchedOn || option.matchedOn === 'name') return '';
    return `Matched on ${option.matchedOn}`;
}

function HighlightedBrandName({ label, query, highlight }) {
    const needle = query.trim();
    if (!highlight || !needle) return label;

    const labelLower = label.toLowerCase();
    const needleLower = needle.toLowerCase();
    const parts = [];
    let index = 0;

    while (index < label.length) {
        const matchIndex = labelLower.indexOf(needleLower, index);
        if (matchIndex === -1) {
            parts.push({ text: label.slice(index), highlight: false });
            break;
        }
        if (matchIndex > index) {
            parts.push({ text: label.slice(index, matchIndex), highlight: false });
        }
        parts.push({ text: label.slice(matchIndex, matchIndex + needle.length), highlight: true });
        index = matchIndex + needle.length;
    }

    return parts.map((part, partIndex) => part.highlight ? (
        <Box
            key={partIndex}
            component="span"
            sx={{ backgroundColor: '#fef3c7', color: '#92400e', fontWeight: 700, borderRadius: 0.5 }}
        >
            {part.text}
        </Box>
    ) : (
        <React.Fragment key={partIndex}>{part.text}</React.Fragment>
    ));
}

function LogoPreview({ logoUrl, size = 42, imageSize = 38 }) {
    const preview = (
        <Box
            sx={{
                width: size,
                height: size,
                border: '1px solid #e5e7eb',
                borderRadius: 1,
                ...LOGO_CHECKERBOARD_SX,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                overflow: 'hidden',
            }}
        >
            {logoUrl && (
                <Box
                    component="img"
                    src={logoUrl}
                    alt=""
                    onError={e => { e.currentTarget.style.visibility = 'hidden'; }}
                    sx={{ maxWidth: imageSize, maxHeight: imageSize, objectFit: 'contain' }}
                />
            )}
        </Box>
    );

    if (!logoUrl) return preview;

    return (
        <Tooltip
            arrow
            placement="right"
            title={(
                <Box
                    sx={{
                        width: 160,
                        height: 120,
                        borderRadius: 1,
                        ...LOGO_CHECKERBOARD_SX,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 1,
                    }}
                >
                    <Box
                        component="img"
                        src={logoUrl}
                        alt=""
                        sx={{ maxWidth: 144, maxHeight: 104, objectFit: 'contain' }}
                    />
                </Box>
            )}
        >
            {preview}
        </Tooltip>
    );
}

// ─── Zoomable / pannable image ────────────────────────────────────────────────

function ZoomableImage({ src, height, controlsEnabled, onActivate }) {
    const containerRef = useRef();
    const [zoom, setZoom]     = useState(1);
    const [pan, setPan]       = useState({ x: 0, y: 0 });
    const [dragging, setDragging] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [internalControlsEnabled, setInternalControlsEnabled] = useState(false);
    const zoomRef   = useRef(1);
    const panRef    = useRef({ x: 0, y: 0 });
    const lastMouse = useRef({ x: 0, y: 0 });
    const controlsAreControlled = typeof controlsEnabled === 'boolean';
    const imageControlsEnabled = controlsAreControlled ? controlsEnabled : internalControlsEnabled;
    const imageControlsEnabledRef = useRef(imageControlsEnabled);

    useEffect(() => {
        imageControlsEnabledRef.current = imageControlsEnabled;
    }, [imageControlsEnabled]);

    const update = (newZoom, newPan) => {
        zoomRef.current = newZoom;
        panRef.current  = newPan;
        setZoom(newZoom);
        setPan(newPan);
    };

    const reset = () => update(1, { x: 0, y: 0 });
    const activateControls = () => {
        if (!controlsAreControlled) {
            setInternalControlsEnabled(true);
        }
        if (onActivate) {
            onActivate();
        }
    };

    // Reset when image changes (e.g. carousel nav)
    useEffect(() => { reset(); }, [src]); // eslint-disable-line react-hooks/exhaustive-deps

    // Non-passive wheel: zoom to cursor
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const onWheel = (e) => {
            if (!imageControlsEnabledRef.current) return;
            e.preventDefault();
            const rect = el.getBoundingClientRect();
            const mx = e.clientX - (rect.left + rect.width  / 2);
            const my = e.clientY - (rect.top  + rect.height / 2);
            const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
            const z = zoomRef.current;
            const p = panRef.current;
            const newZ = Math.min(Math.max(z * factor, 0.5), 8);
            const ratio = newZ / z;
            update(newZ, {
                x: mx * (1 - ratio) + p.x * ratio,
                y: my * (1 - ratio) + p.y * ratio,
            });
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, []); // safe: uses refs

    // Drag pan (attached to document to survive leaving the container)
    useEffect(() => {
        if (!dragging) return;
        const onMove = (e) => {
            const dx = e.clientX - lastMouse.current.x;
            const dy = e.clientY - lastMouse.current.y;
            lastMouse.current = { x: e.clientX, y: e.clientY };
            const newPan = { x: panRef.current.x + dx, y: panRef.current.y + dy };
            panRef.current = newPan;
            setPan(newPan);
        };
        const onUp = () => setDragging(false);
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup',   onUp);
        return () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup',   onUp);
        };
    }, [dragging]);

    // Toolbar zoom (from image center)
    const zoomBy = (factor) => {
        const z = zoomRef.current;
        const p = panRef.current;
        const newZ = Math.min(Math.max(z * factor, 0.5), 8);
        const ratio = newZ / z;
        update(newZ, { x: p.x * ratio, y: p.y * ratio });
    };

    return (
        <Box
            ref={containerRef}
            sx={{
                position: 'relative', height,
                backgroundColor: IMG_BG, overflow: 'hidden',
                cursor: !imageControlsEnabled ? 'pointer' : dragging ? 'grabbing' : zoom > 1 ? 'grab' : 'zoom-in',
                userSelect: 'none',
            }}
            onMouseDown={e => {
                if (!imageControlsEnabled) {
                    activateControls();
                    return;
                }
                e.preventDefault();
                lastMouse.current = { x: e.clientX, y: e.clientY };
                setDragging(true);
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onDoubleClick={reset}
        >
            <img
                src={src}
                alt=""
                draggable={false}
                style={{
                    position: 'absolute', top: 0, left: 0,
                    width: '100%', height: '100%',
                    objectFit: 'contain',
                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                    transformOrigin: 'center center',
                    filter: !imageControlsEnabled && hovered ? 'grayscale(0.25) brightness(0.88)' : 'none',
                    transition: 'filter 0.18s ease',
                    pointerEvents: 'none',
                }}
                onError={e => { e.target.style.display = 'none'; }}
            />

            {!imageControlsEnabled && hovered && (
                <Box sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.75,
                    pointerEvents: 'none',
                    zIndex: 2,
                }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1.1,
                        py: 0.65,
                        borderRadius: 1,
                        backgroundColor: 'rgba(0,0,0,.58)',
                        color: '#fff',
                        border: '1px solid rgba(255,255,255,.16)',
                        boxShadow: '0 8px 24px rgba(0,0,0,.24)',
                    }}>
                        <CenterFocusStrongIcon sx={{ fontSize: 15 }} />
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700 }}>
                            Click for actions
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* Zoom toolbar */}
            {imageControlsEnabled && (
                <Box
                    onMouseDown={e => e.stopPropagation()}
                    sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', flexDirection: 'column', gap: 0.5, zIndex: 2 }}
                >
                    <Tooltip title="Zoom in" placement="left">
                        <IconButton size="small" onClick={() => zoomBy(1.25)}
                            sx={IMAGE_CONTROL_BUTTON_SX}>
                            <ZoomInIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Zoom out" placement="left">
                        <IconButton size="small" onClick={() => zoomBy(1 / 1.25)}
                            sx={IMAGE_CONTROL_BUTTON_SX}>
                            <ZoomOutIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Reset" placement="left">
                        <IconButton size="small" onClick={reset}
                            sx={IMAGE_CONTROL_BUTTON_SX}>
                            <CenterFocusStrongIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}

            {/* Zoom level badge */}
            {imageControlsEnabled && zoom !== 1 && (
                <Box sx={{ position: 'absolute', bottom: 8, left: 8, bgcolor: 'rgba(0,0,0,.55)', borderRadius: 0.5, px: 0.75, py: 0.25, zIndex: 2 }}>
                    <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,.8)', fontVariantNumeric: 'tabular-nums' }}>
                        {Math.round(zoom * 100)}%
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

// ─── Box Images tab: carousel + zoom ─────────────────────────────────────────

function BoxImagesTab({ media, height }) {
    const similar   = media.similarSnapshotUrls ?? [];
    const allImages = [media.snapshotUrl, ...similar];
    const hasMany   = allImages.length > 1;
    const [idx, setIdx] = useState(0);
    const [controlsEnabled, setControlsEnabled] = useState(false);

    const prev = () => setIdx(i => (i - 1 + allImages.length) % allImages.length);
    const next = () => setIdx(i => (i + 1) % allImages.length);

    return (
        <Box sx={{ backgroundColor: '#111' }}>
            <Box sx={{ position: 'relative' }}>
                <ZoomableImage
                    src={allImages[idx]}
                    height={height}
                    controlsEnabled={controlsEnabled}
                    onActivate={() => setControlsEnabled(true)}
                />

                {hasMany && (
                    <>
                    <Box sx={{
                        position: 'absolute', left: 0, top: 0, bottom: 0, width: 72, zIndex: 2,
                        background: 'linear-gradient(90deg, rgba(0,0,0,.62), rgba(0,0,0,0))',
                        pointerEvents: 'none',
                    }} />
                    <Box sx={{
                        position: 'absolute', right: 0, top: 0, bottom: 0, width: 72, zIndex: 2,
                        background: 'linear-gradient(270deg, rgba(0,0,0,.62), rgba(0,0,0,0))',
                        pointerEvents: 'none',
                    }} />
                    <Box sx={{
                        position: 'absolute', top: 8, left: 8, zIndex: 3,
                        bgcolor: 'rgba(0,0,0,.68)', color: '#fff',
                        border: '1px solid rgba(255,255,255,.18)',
                        borderRadius: 1,
                        px: 0.8, py: 0.35,
                    }}>
                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                            {idx + 1}/{allImages.length}
                        </Typography>
                    </Box>
                    <IconButton onClick={prev} size="small" sx={{
                        position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                        ...IMAGE_NAV_BUTTON_SX,
                        zIndex: 3,
                    }}>
                        <ChevronLeftIcon sx={{ fontSize: 26 }} />
                    </IconButton>
                    <IconButton onClick={next} size="small" sx={{
                        position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                        ...IMAGE_NAV_BUTTON_SX,
                        zIndex: 3,
                    }}>
                        <ChevronRightIcon sx={{ fontSize: 26 }} />
                    </IconButton>
                </>
            )}
            </Box>

            {hasMany && (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    overflowX: 'auto',
                    borderTop: '1px solid #1f1f1f',
                    px: 1,
                    py: 0.85,
                }}>
                    {allImages.map((_, i) => (
                        <Box
                            key={i}
                            component="button"
                            type="button"
                            onClick={() => {
                                setIdx(i);
                                setControlsEnabled(true);
                            }}
                            sx={{
                                width: 54,
                                height: 38,
                                p: 0,
                                borderRadius: 1,
                                border: i === idx ? '2px solid #fff' : '1px solid rgba(255,255,255,.28)',
                                bgcolor: 'rgba(0,0,0,.72)',
                                overflow: 'hidden',
                                cursor: 'pointer',
                                opacity: i === idx ? 1 : 0.68,
                                boxShadow: i === idx ? '0 0 0 2px rgba(99,102,241,.55)' : 'none',
                                flex: '0 0 auto',
                                transition: 'opacity 0.18s, border-color 0.18s, transform 0.18s',
                                '&:hover': { opacity: 1, transform: 'translateY(-1px)' },
                            }}
                        >
                            <Box
                                component="img"
                                src={allImages[i]}
                                alt=""
                                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                onError={e => { e.currentTarget.style.visibility = 'hidden'; }}
                            />
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
}

// ─── Media viewer: Box Images / Frame / Video tabs ────────────────────────────

const MEDIA_TAB_SX = {
    bgcolor: '#111',
    borderBottom: '1px solid #1f1f1f',
    '& .MuiTab-root': {
        fontSize: '0.72rem', fontWeight: 500, minHeight: 34,
        color: 'rgba(255,255,255,.45)', textTransform: 'none', px: 2, py: 0,
        '&.Mui-selected': { color: '#fff' },
    },
    '& .MuiTabs-indicator': { backgroundColor: '#fff', height: 2 },
};

function MediaViewer({ media, imageHeight = 280 }) {
    const [tab, setTab] = useState('box');
    const hasSimilar = (media.similarSnapshotUrls?.length ?? 0) > 0;

    return (
        <Box>
            <Box sx={MEDIA_TAB_SX}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 34 }}>
                    <Tab value="box"   label={hasSimilar ? 'Box images' : 'Box image'} />
                    <Tab value="frame" label="Frame" />
                    <Tab value="video" label="Video" />
                </Tabs>
            </Box>

            {tab === 'box'   && <BoxImagesTab media={media} height={imageHeight} />}
            {tab === 'frame' && <ZoomableImage src={media.snapshotNoBoundingBoxUrl} height={imageHeight} />}
            {tab === 'video' && (
                <Box>
                    <Box sx={{ height: imageHeight, bgcolor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {media.clipUrl ? (
                            <iframe
                                src={media.clipUrl}
                                title="Clip"
                                style={{ width: '100%', height: imageHeight, border: 'none', display: 'block' }}
                                allowFullScreen
                            />
                        ) : (
                            <Typography sx={{ color: 'rgba(255,255,255,.35)', fontSize: '0.82rem' }}>
                                No video available
                            </Typography>
                        )}
                    </Box>
                    {media.clipUrl && (
                        <Box sx={{ display: 'flex', gap: 1, px: 1.5, py: 0.75, bgcolor: '#111', borderTop: '1px solid #1f1f1f' }}>
                            <Button size="small" variant="outlined"
                                onClick={() => window.open(media.clipUrl, '_blank')}
                                sx={{ fontSize: '0.72rem', borderColor: '#333', color: '#ccc', textTransform: 'none', '&:hover': { borderColor: '#666', bgcolor: '#1a1a1a' } }}>
                                Open clip
                            </Button>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
}

// ─── Dev meta collapsible ─────────────────────────────────────────────────────

function DevMeta({ meta }) {
    const [open, setOpen] = useState(false);
    if (!meta) return null;
    const entries = Object.entries(meta).filter(([, v]) => v !== null && v !== undefined && v !== '');
    if (entries.length === 0) return null;

    return (
        <Box sx={{ borderTop: '1px solid #f0f0f0' }}>
            <Button
                size="small"
                disableRipple
                onClick={() => setOpen(o => !o)}
                endIcon={open ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                sx={{
                    m: 1, ml: 1.5,
                    color: '#888',
                    fontWeight: 400,
                    fontSize: '0.72rem',
                    letterSpacing: '0.03em',
                    textTransform: 'none',
                    '&:hover': { backgroundColor: 'transparent', color: '#555' },
                }}
            >
                Fixture info · {entries.length} fields
            </Button>
            <Collapse in={open}>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    px: 2, pb: 2, pt: 0.5,
                    gap: '4px 16px',
                }}>
                    {entries.map(([k, v]) => (
                        <Box key={k} sx={{ py: 0.75, borderBottom: '1px solid #f5f5f5' }}>
                            <Typography sx={{ fontSize: '0.67rem', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.25 }}>
                                {k}
                            </Typography>
                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#222' }}>
                                {String(v)}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Collapse>
        </Box>
    );
}

// ─── Example images sub-component ────────────────────────────────────────────

function ExampleImages({ urls, files, onAddUrl, onRemoveUrl, onAddFiles, onRemoveFile }) {
    const [urlInput, setUrlInput] = useState('');
    const fileInputRef = React.useRef();

    const handleAddUrl = () => {
        const trimmed = urlInput.trim();
        if (!trimmed) return;
        onAddUrl(trimmed);
        setUrlInput('');
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); handleAddUrl(); }
    };

    const allItems = [
        ...urls.map((u, i) => ({ key: `url-${i}`, label: u, isFile: false, index: i })),
        ...files.map((f, i) => ({ key: `file-${i}`, label: f.name, isFile: true, index: i, preview: URL.createObjectURL(f) })),
    ];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9ca3af' }}>
                Example images
            </Typography>

            {/* Thumbnail strip */}
            {allItems.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {allItems.map(item => (
                        <Box key={item.key} sx={{
                            position: 'relative',
                            width: 72, height: 72,
                            borderRadius: 1,
                            overflow: 'hidden',
                            border: '1px solid #e5e7eb',
                            backgroundColor: '#f9fafb',
                            flexShrink: 0,
                        }}>
                            <img
                                src={item.isFile ? item.preview : item.label}
                                alt=""
                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                onError={e => { e.target.style.display = 'none'; }}
                            />
                            {/* URL badge */}
                            {!item.isFile && (
                                <Box sx={{
                                    position: 'absolute', bottom: 0, left: 0, right: 0,
                                    backgroundColor: 'rgba(0,0,0,.55)',
                                    px: 0.5, py: 0.25,
                                }}>
                                    <Typography sx={{ fontSize: '0.55rem', color: '#fff', noWrap: true, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        URL
                                    </Typography>
                                </Box>
                            )}
                            <IconButton
                                size="small"
                                onClick={() => item.isFile ? onRemoveFile(item.index) : onRemoveUrl(item.index)}
                                sx={{
                                    position: 'absolute', top: 2, right: 2,
                                    width: 18, height: 18,
                                    backgroundColor: 'rgba(0,0,0,.6)',
                                    color: '#fff',
                                    '&:hover': { backgroundColor: 'rgba(0,0,0,.85)' },
                                }}
                            >
                                <CloseIcon sx={{ fontSize: 11 }} />
                            </IconButton>
                        </Box>
                    ))}
                </Box>
            )}

            {/* Add controls */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                    size="small"
                    placeholder="https://example.com/logo.jpg"
                    value={urlInput}
                    onChange={e => setUrlInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={handleAddUrl}
                    sx={{ flex: '1 1 200px', fontSize: '0.8rem' }}
                />
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddLinkIcon sx={{ fontSize: 16 }} />}
                    onClick={handleAddUrl}
                    disabled={!urlInput.trim()}
                    sx={{ fontSize: '0.75rem', borderColor: '#e5e7eb', color: '#374151', whiteSpace: 'nowrap' }}
                >
                    Add URL
                </Button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: 'none' }}
                    onChange={e => { onAddFiles(Array.from(e.target.files)); e.target.value = ''; }}
                />
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<UploadFileIcon sx={{ fontSize: 16 }} />}
                    onClick={() => fileInputRef.current.click()}
                    sx={{ fontSize: '0.75rem', borderColor: '#e5e7eb', color: '#374151', whiteSpace: 'nowrap' }}
                >
                    Upload files
                </Button>
            </Box>
        </Box>
    );
}

// ─── Feedback form ────────────────────────────────────────────────────────────

function getCardMediaDetectionId(card) {
    return card?.media?.mediaDetectionId ?? card?.mediaDetectionId ?? card?.media?.id ?? null;
}

function getFeedbackBrandOption(feedback) {
    if (!feedback || typeof feedback !== 'object') return null;

    const brand = feedback.brand && typeof feedback.brand === 'object' ? feedback.brand : null;
    const brandId = feedback.brandId ?? brand?.brandId ?? brand?.id ?? null;
    const brandName = feedback.brandName
        ?? brand?.name
        ?? brand?.brandName
        ?? brand?.displayName
        ?? brand?.meta?.['.name']
        ?? '';

    if (!brand && brandId === null) return null;

    return {
        ...(brand ?? {}),
        ...(brandName ? { name: brandName } : {}),
        ...(brandId !== null ? { brandId } : {}),
    };
}

function FeedbackForm({ card, groupHash, onSaved, readOnly }) {
    const existing = card.feedback;
    const existingBrand = getFeedbackBrandOption(existing);
    const existingBrandName = existing?.brandName || getBrandOptionLabel(existingBrand);

    const [decision, setDecision]       = useState(existing?.decision ?? 'apply');
    const [brandName, setBrandName]     = useState(existingBrandName);
    const [selectedBrand, setSelectedBrand] = useState(existingBrand);
    const [brandUrl, setBrandUrl]       = useState(existing?.brandUrl ?? '');
    const [textStrings, setTextStrings] = useState(
        existing?.textStrings?.map(t => t.text).join('\n') ?? ''
    );
    const [exampleUrls, setExampleUrls] = useState(existing?.exampleUrls ?? []);
    const [exampleFiles, setExampleFiles] = useState([]);
    const [tags, setTags]               = useState(existing?.meta ?? {});
    const [pendingTag, setPendingTag]   = useState({ tag: '', value: '' });
    const [comment, setComment]         = useState(existing?.comment ?? '');
    const [submitting, setSubmitting]   = useState(false);
    const [saved, setSaved]             = useState(!!existing || readOnly);
    const [brandOptions, setBrandOptions] = useState([]);
    const [brandSearchLoading, setBrandSearchLoading] = useState(false);
    const selectedBrandId = getBrandOptionId(selectedBrand);
    const selectedBrandLogoUrl = getBrandOptionLogoUrl(selectedBrand);
    const brandUrlRequired = decision === 'apply' && selectedBrandId === null && brandName.trim().length > 0;
    const brandUrlMissing = brandUrlRequired && !brandUrl.trim();

    useEffect(() => {
        const q = brandName.trim();
        const selectedBrandLabel = getBrandOptionLabel(selectedBrand);

        if (
            saved ||
            readOnly ||
            decision !== 'apply' ||
            q.length < 2 ||
            (selectedBrand && q === selectedBrandLabel)
        ) {
            setBrandOptions([]);
            setBrandSearchLoading(false);
            return;
        }

        let active = true;
        const timeoutId = setTimeout(() => {
            setBrandSearchLoading(true);
            DataService.searchLibraryBrands(q)
                .then(brands => {
                    if (!active) return;
                    setBrandOptions(brands.filter(brand => getBrandOptionLabel(brand)));
                })
                .finally(() => {
                    if (active) setBrandSearchLoading(false);
                });
        }, 300);

        return () => {
            active = false;
            clearTimeout(timeoutId);
        };
    }, [brandName, decision, readOnly, saved, selectedBrand]);

    const handleSubmit = async () => {
        if (brandUrlMissing) {
            toast.error('Brand URL is required for new brand names');
            return;
        }

        const feedback = { decision };
        const savedFeedback = { decision };
        if (decision === 'apply') {
            const trimmedBrandName = brandName.trim();
            if (trimmedBrandName) {
                savedFeedback.brandName = trimmedBrandName;
                if (selectedBrandId !== null) feedback.brandId = selectedBrandId;
                else feedback.brandName = trimmedBrandName;
            }
            if (selectedBrandId !== null) savedFeedback.brandId = selectedBrandId;
            if (selectedBrandId === null && brandUrl.trim()) {
                feedback.brandUrl = brandUrl.trim();
                savedFeedback.brandUrl = brandUrl.trim();
            }
            if (textStrings.trim()) {
                feedback.textStrings = textStrings
                    .split('\n').map(t => t.trim()).filter(Boolean)
                    .map(t => ({ text: t, threshold: 0.8 }));
                savedFeedback.textStrings = feedback.textStrings;
            }
            if (exampleUrls.length > 0) {
                feedback.exampleUrls = exampleUrls;
                savedFeedback.exampleUrls = exampleUrls;
            }
            // Include a tag typed into the inputs but not yet added via "Add New Tag"
            const pendingTagName = pendingTag.tag.trim();
            const meta = pendingTagName && pendingTag.value && pendingTagName !== '.name'
                ? { ...tags, [pendingTagName]: pendingTag.value }
                : tags;
            if (Object.keys(meta).length > 0) {
                feedback.meta = meta;
                savedFeedback.meta = meta;
            }
        }
        if (comment.trim()) {
            feedback.comment = comment.trim();
            savedFeedback.comment = comment.trim();
        }

        const mediaDetectionId = getCardMediaDetectionId(card);
        if (mediaDetectionId === null) {
            toast.error('Cannot save feedback: missing media detection id');
            return;
        }

        setSubmitting(true);
        try {
            await DataService.submitAuditFeedback(groupHash, mediaDetectionId, feedback, exampleFiles);
            const { cards: refreshedCards } = await DataService.getAuditCards(groupHash);
            const refreshedCard = refreshedCards.find(c => (
                String(getCardMediaDetectionId(c)) === String(mediaDetectionId)
            ));
            const persistedFeedback = refreshedCard?.feedback;

            if (!persistedFeedback) {
                throw new Error('Feedback was not saved by the API');
            }

            const confirmedFeedback = { ...savedFeedback, ...persistedFeedback };
            toast.success('Feedback saved');
            if (savedFeedback.meta) setTags(savedFeedback.meta);
            setSaved(true);
            onSaved(confirmedFeedback);
        } catch (error) {
            console.error('Failed to save audit feedback', error);
            toast.error(error?.message || 'Failed to save feedback');
        } finally {
            setSubmitting(false);
        }
    };

    if (saved) {
        const thumbUrls  = exampleUrls;
        const thumbFiles = exampleFiles;
        const totalImages = thumbUrls.length + thumbFiles.length;

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                {/* Saved header bar */}
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    px: 2, py: 1,
                    backgroundColor: '#f0fdf4',
                    borderBottom: '1px solid #bbf7d0',
                }}>
                    <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 15, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#16a34a' }}>
                        Feedback saved
                    </Typography>
                    {!readOnly && (
                        <Button size="small" onClick={() => setSaved(false)}
                            sx={{ ml: 'auto', color: '#6b7280', fontSize: '0.72rem', minWidth: 0, px: 1, py: 0 }}>
                            Edit
                        </Button>
                    )}
                </Box>

                {/* Summary body */}
                <Box sx={{ px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {/* Decision chip */}
                    {
                        decision !== 'apply' && (
                            <Chip
                            label={'Discard'}
                            size="small"
                            sx={{
                                alignSelf: 'flex-start',
                                backgroundColor: '#f4f4f5',
                                color: '#52525b',
                                fontWeight: 700, fontSize: '0.7rem',
                            }}
                        />
                        )
                    }


                    {decision === 'apply' && (
                        <>
                            {(brandName || brandUrl) && (
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    {brandName && (
                                        <Box>
                                            <Typography sx={{ fontSize: '0.63rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9ca3af', mb: 0.2 }}>Brand</Typography>
                                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#111' }}>{brandName}</Typography>
                                        </Box>
                                    )}
                                    {brandUrl && (
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography sx={{ fontSize: '0.63rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9ca3af', mb: 0.2 }}>URL</Typography>
                                            <Typography sx={{ fontSize: '0.78rem', color: '#374151', wordBreak: 'break-all' }}>{brandUrl}</Typography>
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {textStrings.trim() && (
                                <Box>
                                    <Typography sx={{ fontSize: '0.63rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9ca3af', mb: 0.3 }}>
                                        Text strings
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.8rem', color: '#374151', whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                                        {textStrings.trim()}
                                    </Typography>
                                </Box>
                            )}

                            {totalImages > 0 && (
                                <Box>
                                    <Typography sx={{ fontSize: '0.63rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9ca3af', mb: 0.5 }}>
                                        Example images · {totalImages}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                        {thumbUrls.map((url, i) => (
                                            <Box
                                                key={`u${i}`}
                                                component="img"
                                                src={url}
                                                alt=""
                                                sx={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 1, border: '1px solid #e5e7eb' }}
                                                onError={e => { e.target.style.display = 'none'; }}
                                            />
                                        ))}
                                        {thumbFiles.map((f, i) => (
                                            <Box
                                                key={`f${i}`}
                                                component="img"
                                                src={URL.createObjectURL(f)}
                                                alt=""
                                                sx={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 1, border: '1px solid #e5e7eb' }}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {Object.keys(tags).length > 0 && (
                                <Box>
                                    <Typography sx={{ fontSize: '0.63rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9ca3af', mb: 0.5 }}>
                                        Tags · {Object.keys(tags).length}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                        {Object.entries(tags).map(([key, value]) => (
                                            <Chip key={key} label={`${key}: ${value}`} size="small"
                                                sx={{ backgroundColor: '#f4f4f5', color: '#374151', fontSize: '0.72rem' }} />
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </>
                    )}

                    {comment.trim() && (
                        <Box>
                            <Typography sx={{ fontSize: '0.63rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9ca3af', mb: 0.2 }}>Comment</Typography>
                            <Typography sx={{ fontSize: '0.8rem', color: '#6b7280', fontStyle: 'italic' }}>
                                "{comment.trim()}"
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        );
    }

    if (readOnly) {
        return (
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                px: 2, py: 2,
                color: '#9ca3af',
            }}>
                <Typography sx={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
                    No feedback provided
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Decision toggle */}
            <Box>
                <Typography sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9ca3af', mb: 1 }}>
                    Decision
                </Typography>
                <ToggleButtonGroup
                    value={decision}
                    exclusive
                    onChange={(_, v) => { if (v) setDecision(v); }}
                    size="small"
                    sx={{
                        '& .MuiToggleButton-root': {
                            px: 2.5, py: 0.75,
                            fontSize: '0.78rem', fontWeight: 500,
                            textTransform: 'none', borderColor: '#e5e7eb', color: '#6b7280',
                        },
                        '& .MuiToggleButton-root.Mui-selected[value="apply"]': {
                            backgroundColor: '#f0fdf4', color: '#15803d', borderColor: '#86efac',
                        },
                        '& .MuiToggleButton-root.Mui-selected[value="discard"]': {
                            backgroundColor: '#fafafa', color: '#374151', borderColor: '#d1d5db',
                        },
                    }}
                >
                    <ToggleButton value="apply">Apply corrections</ToggleButton>
                    <ToggleButton value="discard">Discard</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* Apply-only fields */}
            {decision === 'apply' && (
                <>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                        <Autocomplete
                            freeSolo
                            size="small"
                            clearOnBlur={false}
                            filterOptions={options => options}
                            getOptionLabel={getBrandOptionLabel}
                            inputValue={brandName}
                            isOptionEqualToValue={(option, value) => (
                                getBrandOptionLabel(option) === getBrandOptionLabel(value)
                            )}
                            loading={brandSearchLoading}
                            loadingText="Searching brands..."
                            noOptionsText={brandName.trim().length < 2 ? 'Type at least 2 characters' : 'No matching brands'}
                            onChange={(_, value) => {
                                if (typeof value === 'string') {
                                    setSelectedBrand(null);
                                    setBrandName(value);
                                    return;
                                }
                                if (value) {
                                    setSelectedBrand(value);
                                    setBrandName(getBrandOptionLabel(value));
                                    setBrandUrl('');
                                    return;
                                }
                                setSelectedBrand(null);
                                setBrandName('');
                            }}
                            onInputChange={(_, value, reason) => {
                                setBrandName(value);
                                if (reason === 'input' || reason === 'clear') {
                                    setSelectedBrand(null);
                                }
                            }}
                            options={brandOptions}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Brand name"
                                    placeholder="Type or choose a brand"
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <>
                                                {selectedBrandId !== null && selectedBrandLogoUrl && (
                                                    <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                                                        <LogoPreview logoUrl={selectedBrandLogoUrl} size={28} imageSize={24} />
                                                    </Box>
                                                )}
                                                {params.InputProps.startAdornment}
                                            </>
                                        ),
                                        endAdornment: (
                                            <>
                                                {brandSearchLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            renderOption={(props, option) => {
                                const label = getBrandOptionLabel(option);
                                const logoUrl = getBrandOptionLogoUrl(option);
                                const matchReason = getBrandMatchReason(option);
                                return (
                                    <Box
                                        component="li"
                                        {...props}
                                        key={getBrandOptionId(option) ?? label}
                                        sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}
                                    >
                                        <LogoPreview logoUrl={logoUrl} />
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography sx={{ fontSize: '0.84rem', fontWeight: 500, color: '#111827' }}>
                                                <HighlightedBrandName
                                                    label={label}
                                                    query={brandName}
                                                    highlight={option?.matchedOn === 'name'}
                                                />
                                            </Typography>
                                            {matchReason && (
                                                <Typography sx={{ fontSize: '0.7rem', color: '#9ca3af', mt: 0.25 }}>
                                                    {matchReason}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                );
                            }}
                            value={selectedBrand ?? brandName}
                            sx={{ flex: '1 1 160px' }}
                        />
                        {selectedBrandId === null && (
                            <TextField
                                label="Brand URL"
                                size="small"
                                required={brandUrlRequired}
                                error={brandUrlMissing}
                                helperText={brandUrlRequired ? 'Required for new brand names' : ' '}
                                value={brandUrl}
                                onChange={e => setBrandUrl(e.target.value)}
                                sx={{ flex: '1 1 160px' }}
                            />
                        )}
                    </Box>
                    <TextField
                        label="Text strings (one per line)"
                        size="small"
                        multiline
                        minRows={2}
                        value={textStrings}
                        onChange={e => setTextStrings(e.target.value)}
                        placeholder={"coca cola\nshare the road"}
                    />
                    <ExampleImages
                        urls={exampleUrls}
                        files={exampleFiles}
                        onAddUrl={u => setExampleUrls(prev => [...prev, u])}
                        onRemoveUrl={i => setExampleUrls(prev => prev.filter((_, idx) => idx !== i))}
                        onAddFiles={fs => setExampleFiles(prev => [...prev, ...fs])}
                        onRemoveFile={i => setExampleFiles(prev => prev.filter((_, idx) => idx !== i))}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9ca3af' }}>
                            Tags
                        </Typography>
                        <ItemTagManager logoTags={tags} setLogoTags={setTags} onPendingTagChange={setPendingTag} />
                    </Box>
                </>
            )}

            <TextField
                label="Comment"
                size="small"
                value={comment}
                onChange={e => setComment(e.target.value)}
                placeholder="Optional note"
            />

            <Box>
                <Button
                    variant="contained"
                    size="small"
                    onClick={handleSubmit}
                    disabled={submitting || brandUrlMissing}
                    disableElevation
                    sx={{
                        backgroundColor: '#1d1d1f', color: '#fff', fontWeight: 600, px: 3,
                        '&:hover': { backgroundColor: '#111' },
                        '&.Mui-disabled': { backgroundColor: '#e5e7eb', color: '#9ca3af' },
                    }}
                >
                    {submitting
                        ? <><CircularProgress size={12} sx={{ mr: 1 }} color="inherit" />Saving…</>
                        : 'Save feedback'}
                </Button>
            </Box>
        </Box>
    );
}

// ─── Card shell ───────────────────────────────────────────────────────────────

function CardShell({ accentColor, children }) {
    return (
        <Box sx={{
            borderRadius: CARD_RADIUS,
            overflow: 'hidden',
            backgroundColor: SURFACE,
            boxShadow: CARD_SHADOW,
            borderLeft: `4px solid ${accentColor}`,
        }}>
            {children}
        </Box>
    );
}

// ─── Card header ─────────────────────────────────────────────────────────────

function CardHeader({ label, labelColor, labelBg, brandName, placementName, logoVersionActivated }) {
    return (
        <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            px: 2, py: 1.25,
            borderBottom: '1px solid #f3f4f6',
            flexWrap: 'wrap',
        }}>
            <Chip
                label={label}
                size="small"
                sx={{
                    backgroundColor: labelBg,
                    color: labelColor,
                    fontWeight: 700,
                    fontSize: '0.68rem',
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    height: 22,
                    flexShrink: 0,
                }}
            />

            {brandName ? (
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#111', letterSpacing: '-0.01em' }}>
                    {brandName}
                </Typography>
            ) : (
                <Typography sx={{ fontSize: '0.82rem', color: '#aaa', fontStyle: 'italic' }}>
                    No brand
                </Typography>
            )}

            {logoVersionActivated === true && (
                <Chip label="Logo activated" size="small" sx={{ backgroundColor: '#f0fdf4', color: '#15803d', fontSize: '0.68rem', height: 20 }} />
            )}

            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LocationOnIcon sx={{ fontSize: 13, color: '#9ca3af' }} />
                <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>
                    {placementName}
                </Typography>
            </Box>
        </Box>
    );
}

// ─── Needs Attention card ─────────────────────────────────────────────────────

function NeedsAttentionCard({ card, groupHash, onSaved, readOnly }) {
    const { media, activation } = card;
    const [savedFeedback, setSavedFeedback] = useState(card.feedback ?? null);

    const handleFeedbackSaved = (feedback) => {
        setSavedFeedback(feedback);
        onSaved(media.mediaDetectionId, feedback);
    };

    const headerBrandName = savedFeedback?.brandName || savedFeedback?.brand?.name || activation.brandName;

    return (
        <CardShell accentColor={ACCENT_ATTENTION}>
            <CardHeader
                label="Needs attention"
                labelColor="#92400e"
                labelBg="#fef3c7"
                brandName={headerBrandName}
                placementName={media.placementName}
            />

            <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                {/* Image panel */}
                <Box sx={{ width: { xs: '100%', md: '55%' }, minWidth: 280 }}>
                    <MediaViewer media={media} imageHeight={300} />
                </Box>

                {/* Right panel */}
                <Box sx={{
                    flex: 1, minWidth: 260,
                    display: 'flex', flexDirection: 'column',
                    borderLeft: { md: '1px solid #f3f4f6' },
                }}>
                    {activation.textString && (
                        <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
                            <Typography sx={{ fontSize: '0.67rem', textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9ca3af', mb: 0.75 }}>
                                Detected text
                            </Typography>
                            <Typography sx={{ fontSize: '0.85rem', color: '#374151', fontStyle: 'italic', lineHeight: 1.5 }}>
                                "{activation.textString}"
                            </Typography>
                        </Box>
                    )}
                    {activation.textString && <Divider sx={{ mx: 2 }} />}

                    <Box sx={{ flex: 1 }}>
                        <FeedbackForm card={card} groupHash={groupHash} onSaved={handleFeedbackSaved} readOnly={readOnly} />
                    </Box>
                </Box>
            </Box>

            <DevMeta meta={media.developerMeta} />
        </CardShell>
    );
}

// ─── Activation card ──────────────────────────────────────────────────────────

function ActivationCard({ card }) {
    const { media, activation } = card;
    return (
        <CardShell accentColor={ACCENT_ACTIVATION}>
            <CardHeader
                label="Activation"
                labelColor="#3730a3"
                labelBg="#eef2ff"
                brandName={activation.brandName}
                placementName={media.placementName}
                logoVersionActivated={activation.logoVersionActivated}
            />

            <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                {/* Image panel */}
                <Box sx={{ width: { xs: '100%', md: '55%' }, minWidth: 280 }}>
                    <MediaViewer media={media} imageHeight={260} />
                </Box>

                {/* Info panel */}
                <Box sx={{
                    flex: 1, minWidth: 220, p: 2.5,
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '16px 24px',
                    alignContent: 'start',
                    borderLeft: { md: '1px solid #f3f4f6' },
                }}>
                    <InfoCell label="Brand" value={activation.brandName} />
                    <InfoCell label="Brand ID" value={activation.brandId} mono />
                    <InfoCell
                        label="Logo activated"
                        value={activation.logoVersionActivated ? 'Yes' : 'No'}
                        valueColor={activation.logoVersionActivated ? '#15803d' : '#b91c1c'}
                    />
                    {activation.textString && (
                        <Box sx={{ gridColumn: '1 / -1' }}>
                            <InfoCell label="Detected text" value={`"${activation.textString}"`} italic />
                        </Box>
                    )}
                </Box>
            </Box>

            <DevMeta meta={media.developerMeta} />
        </CardShell>
    );
}

function InfoCell({ label, value, mono = false, italic = false, valueColor }) {
    if (!value && value !== 0 && value !== false) return null;
    return (
        <Box>
            <Typography sx={{ fontSize: '0.67rem', textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9ca3af', mb: 0.4 }}>
                {label}
            </Typography>
            <Typography sx={{
                fontSize: '0.85rem',
                fontWeight: 600,
                color: valueColor ?? '#111827',
                fontStyle: italic ? 'italic' : 'normal',
                fontFamily: mono ? 'monospace' : 'inherit',
            }}>
                {String(value)}
            </Typography>
        </Box>
    );
}

// ─── Section heading ──────────────────────────────────────────────────────────

function SectionHeading({ id, children, count, accentColor, borderColor, bgColor, textColor, icon }) {
    return (
        <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            px: 2.5, py: 1.75,
            background: bgColor,
            border: `1px solid ${borderColor}`,
            borderLeft: `5px solid ${accentColor}`,
            borderRadius: 1.5,
        }}>
            <Box sx={{ color: accentColor, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                {icon}
            </Box>
            <Typography id={id} sx={{ fontSize: '0.95rem', fontWeight: 700, color: textColor, letterSpacing: '0.01em' }}>
                {children}
            </Typography>
            <Chip
                label={`${count} card${count !== 1 ? 's' : ''}`}
                size="small"
                sx={{ ml: 'auto', backgroundColor: accentColor, color: '#fff', fontWeight: 700, fontSize: '0.72rem', flexShrink: 0 }}
            />
        </Box>
    );
}

function CardsSection({ id, title, count, accentColor, borderColor, bgColor, textColor, icon, children }) {
    return (
        <Box
            component="section"
            aria-labelledby={id}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.75,
                p: { xs: 1.25, sm: 2 },
                backgroundColor: '#fff',
                border: `1px solid ${borderColor}`,
                borderRadius: 2,
            }}
        >
            <SectionHeading
                id={id}
                count={count}
                accentColor={accentColor}
                borderColor={borderColor}
                bgColor={bgColor}
                textColor={textColor}
                icon={icon}
            >
                {title}
            </SectionHeading>

            <Box
                component="ul"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    p: 0,
                    m: 0,
                    listStyle: 'none',
                }}
            >
                {children}
            </Box>
        </Box>
    );
}

function CardsTabLabel({ icon, label, count, color }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, minWidth: 0 }}>
            <Box sx={{ color, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                {icon}
            </Box>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'inherit', textTransform: 'none' }}>
                {label}
            </Typography>
            <Chip
                label={count}
                size="small"
                sx={{
                    height: 20,
                    minWidth: 24,
                    backgroundColor: color,
                    color: '#fff',
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    '& .MuiChip-label': { px: 0.75 },
                }}
            />
        </Box>
    );
}

function CompleteFeedbackButton({ allDone, completing, onClick }) {
    return (
        <Tooltip title={allDone ? '' : 'All "Needs attention" cards must have feedback first'}>
            <span>
                <Button
                    variant="contained"
                    size="small"
                    disableElevation
                    startIcon={completing
                        ? <CircularProgress size={13} color="inherit" />
                        : <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
                    onClick={onClick}
                    disabled={!allDone || completing}
                    sx={{
                        backgroundColor: '#16a34a',
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '0.78rem',
                        px: 2,
                        '&:hover': { backgroundColor: '#15803d' },
                        '&.Mui-disabled': { backgroundColor: '#e5e7eb', color: '#9ca3af' },
                    }}
                >
                    {!allDone ? 'Complete all lines' : 'Mark as complete'}
                </Button>
            </span>
        </Tooltip>
    );
}

function getAuditLoadError(error) {
    const response = error?.response;
    const data = response?.data;
    const message =
        data?.errorMessage
        || data?.message
        || (typeof data === 'string' ? data : null)
        || error?.message
        || 'The API did not return audit cards.';

    return {
        message,
        status: response?.status,
        statusText: response?.statusText,
        method: (data?.method || error?.config?.method || '').toUpperCase(),
        requestURI: data?.requestURI || error?.config?.url,
    };
}

function AuditLoadErrorPanel({ error, groupHash, onRetry, onBack }) {
    const statusLabel = error.status
        ? `${error.status}${error.statusText ? ` ${error.statusText}` : ''}`
        : 'Unknown status';

    return (
        <Box sx={{
            backgroundColor: '#fff',
            borderRadius: 2,
            boxShadow: CARD_SHADOW,
            border: '1px solid #fee2e2',
            overflow: 'hidden',
        }}>
            <Alert
                severity="error"
                icon={<WarningAmberIcon />}
                sx={{
                    borderRadius: 0,
                    alignItems: 'flex-start',
                    '& .MuiAlert-message': { width: '100%' },
                }}
            >
                <AlertTitle sx={{ fontWeight: 700 }}>Could not load this logo audit</AlertTitle>
                <Typography sx={{ fontSize: '0.9rem', color: '#7f1d1d', mb: 1 }}>
                    {error.message}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    <Chip size="small" label={`Audit ${groupHash}`} sx={{ fontWeight: 600 }} />
                    <Chip size="small" label={statusLabel} color="error" variant="outlined" />
                    {error.method && <Chip size="small" label={error.method} variant="outlined" />}
                </Box>
                {error.requestURI && (
                    <Typography sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.78rem',
                        color: '#7f1d1d',
                        backgroundColor: '#fff7f7',
                        border: '1px solid #fecaca',
                        borderRadius: 1,
                        px: 1,
                        py: 0.75,
                        mb: 2,
                        wordBreak: 'break-all',
                    }}>
                        {error.requestURI}
                    </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button variant="contained" color="error" size="small" onClick={onRetry}>
                        Retry
                    </Button>
                    <Button variant="outlined" color="error" size="small" onClick={onBack}>
                        Back to audits
                    </Button>
                </Box>
            </Alert>
        </Box>
    );
}

// ─── Detail page ──────────────────────────────────────────────────────────────

export default function ClientFeedbackDetail() {
    const { groupHash } = useParams();
    const history = useHistory();
    const location = useLocation();
    const { url } = useRouteMatch();

    const [cards, setCards]             = useState([]);
    const [groupStatus, setGroupStatus] = useState(null);
    const [loading, setLoading]         = useState(true);
    const [completing, setCompleting]   = useState(false);
    const [activeCardTab, setActiveCardTab] = useState('attention');
    const [auditName, setAuditName] = useState(location.state?.auditName ?? '');
    const [loadError, setLoadError] = useState(null);

    const loadCards = useCallback(() => {
        setLoading(true);
        setLoadError(null);
        DataService.getAuditCards(groupHash)
            .then(({ cards: c, status, name }) => {
                setCards(c);
                setGroupStatus(status);
                if (name) setAuditName(name);
            })
            .catch(error => {
                const nextError = getAuditLoadError(error);
                setCards([]);
                setGroupStatus(null);
                setLoadError(nextError);
                toast.error(nextError.message);
            })
            .finally(() => setLoading(false));
    }, [groupHash]);

    useEffect(() => { loadCards(); }, [loadCards]);

    useEffect(() => {
        if (auditName) return;
        let active = true;
        DataService.getAuditList()
            .then(list => {
                if (!active) return;
                const audit = list.find(item => String(item.hashId) === groupHash);
                if (audit?.name) setAuditName(audit.name);
            })
            .catch(() => {});
        return () => { active = false; };
    }, [auditName, groupHash]);

    const isLocked        = groupStatus !== -2;
    const attentionCards  = cards.filter(c => c.activation?.needsClientAttention);
    const activationCards = cards.filter(c => !c.activation?.needsClientAttention);
    const pendingCount    = attentionCards.filter(c => !c.feedback).length;
    const allDone         = attentionCards.length > 0 && pendingCount === 0;
    const mainListUrl     = url.slice(0, -`/${groupHash}`.length);
    const auditTitle      = auditName || (loading ? 'Loading...' : 'Unnamed audit');
    const visibleCardTab  =
        activeCardTab === 'attention' && attentionCards.length === 0 && activationCards.length > 0
            ? 'activation'
            : activeCardTab === 'activation' && activationCards.length === 0 && attentionCards.length > 0
                ? 'attention'
                : activeCardTab;

    const handleComplete = async () => {
        setCompleting(true);
        try {
            await DataService.completeClientFeedback(groupHash);
            toast.success('Thank you, your feedback is currently being reviewed');
            history.push(mainListUrl);
        } catch {
            toast.error('Failed to mark complete');
        } finally {
            setCompleting(false);
        }
    };

    return (
        <Box sx={{ m: 2 }}>
            {/* Top bar */}
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5,
                px: 2, py: 1.5,
                borderRadius: 1.5,
                backgroundColor: '#fff',
                boxShadow: CARD_SHADOW,
                position: 'sticky',
                top: 16,
                zIndex: 10,
            }}>
                <IconButton size="small" onClick={() => history.goBack()} sx={{ color: '#374151' }}>
                    <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#111', minWidth: 0 }}>
                    Logo Audit: {auditTitle}
                </Typography>

                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
                    {loadError ? (
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={loadCards}
                            sx={{ textTransform: 'none' }}
                        >
                            Retry
                        </Button>
                    ) : !loading && isLocked ? (
                        <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 1,
                            px: 1.5, py: 0.75,
                            backgroundColor: '#f0fdf4',
                            border: '1px solid #bbf7d0',
                            borderRadius: 1.5,
                        }}>
                            <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 16 }} />
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#15803d' }}>
                                Feedback submitted — read only
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {!loading && attentionCards.length > 0 && (
                                <Typography sx={{ fontSize: '0.8rem', color: pendingCount > 0 ? '#92400e' : '#15803d' }}>
                                    {pendingCount > 0
                                        ? `${pendingCount} of ${attentionCards.length} still need feedback`
                                        : `All ${attentionCards.length} reviewed ✓`}
                                </Typography>
                            )}
                            <CompleteFeedbackButton
                                allDone={allDone}
                                completing={completing}
                                onClick={handleComplete}
                            />
                        </>
                    )}
                </Box>
            </Box>

            {/* Content */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
                    <CircularProgress />
                </Box>
            ) : loadError ? (
                <AuditLoadErrorPanel
                    error={loadError}
                    groupHash={groupHash}
                    onRetry={loadCards}
                    onBack={() => history.push(mainListUrl)}
                />
            ) : cards.length === 0 ? (
                <Box sx={{ p: 5, textAlign: 'center', backgroundColor: '#fff', borderRadius: 2, boxShadow: CARD_SHADOW }}>
                    <Typography sx={{ color: '#9ca3af' }}>No audit cards found for this group.</Typography>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{
                        backgroundColor: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: 2,
                        boxShadow: CARD_SHADOW,
                        overflow: 'hidden',
                    }}>
                        <Tabs
                            value={visibleCardTab}
                            onChange={(_, value) => setActiveCardTab(value)}
                            variant="fullWidth"
                            sx={{
                                minHeight: 58,
                                backgroundColor: '#f9fafb',
                                '& .MuiTabs-indicator': {
                                    height: 4,
                                    backgroundColor: visibleCardTab === 'attention' ? ACCENT_ATTENTION : ACCENT_ACTIVATION,
                                },
                                '& .MuiTab-root': {
                                    minHeight: 58,
                                    color: '#6b7280',
                                    borderRight: '1px solid #e5e7eb',
                                    textTransform: 'none',
                                    '&:last-of-type': { borderRight: 0 },
                                    '&.Mui-selected': {
                                        color: '#111827',
                                        backgroundColor: '#fff',
                                    },
                                    '&.Mui-disabled': {
                                        color: '#c0c4cc',
                                    },
                                },
                            }}
                        >
                            <Tab
                                value="attention"
                                disabled={attentionCards.length === 0}
                                label={(
                                    <CardsTabLabel
                                        icon={<WarningAmberIcon sx={{ fontSize: 19 }} />}
                                        label="Needs Attention"
                                        count={attentionCards.length}
                                        color={ACCENT_ATTENTION}
                                    />
                                )}
                            />
                            <Tab
                                value="activation"
                                disabled={activationCards.length === 0}
                                label={(
                                    <CardsTabLabel
                                        icon={<CheckCircleOutlineIcon sx={{ fontSize: 19 }} />}
                                        label="Activated"
                                        count={activationCards.length}
                                        color={ACCENT_ACTIVATION}
                                    />
                                )}
                            />
                        </Tabs>
                    </Box>

                    {visibleCardTab === 'attention' && (
                        <CardsSection
                            id="needs-attention-section"
                            title="Needs Attention"
                            count={attentionCards.length}
                            accentColor={ACCENT_ATTENTION}
                            borderColor="#fed7aa"
                            bgColor="linear-gradient(90deg,#fffbeb 0%,#fef9f0 100%)"
                            textColor="#92400e"
                            icon={<WarningAmberIcon sx={{ fontSize: 22 }} />}
                        >
                            {attentionCards.map((card, i) => (
                                <Box
                                    component="li"
                                    key={card.media?.mediaDetectionId ?? `a${i}`}
                                    sx={{ m: 0 }}
                                >
                                    <NeedsAttentionCard
                                        card={card}
                                        groupHash={groupHash}
                                        readOnly={isLocked}
                                        onSaved={(mediaDetectionId, feedback) => {
                                            setCards(prev => prev.map(c =>
                                                c.media?.mediaDetectionId === mediaDetectionId
                                                    ? { ...c, feedback }
                                                    : c
                                            ));
                                        }}
                                    />
                                </Box>
                            ))}
                        </CardsSection>
                    )}

                    {visibleCardTab === 'activation' && (
                        <CardsSection
                            id="activation-section"
                            title="Activation"
                            count={activationCards.length}
                            accentColor={ACCENT_ACTIVATION}
                            borderColor="#c7d2fe"
                            bgColor="linear-gradient(90deg,#eef2ff 0%,#f5f3ff 100%)"
                            textColor="#3730a3"
                            icon={<CheckCircleOutlineIcon sx={{ fontSize: 22 }} />}
                        >
                            {activationCards.map((card, i) => (
                                <Box
                                    component="li"
                                    key={card.media?.mediaDetectionId ?? `b${i}`}
                                    sx={{ m: 0 }}
                                >
                                    <ActivationCard card={card} />
                                </Box>
                            ))}
                        </CardsSection>
                    )}

                    {!isLocked && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1, pb: 2 }}>
                            <CompleteFeedbackButton
                                allDone={allDone}
                                completing={completing}
                                onClick={handleComplete}
                            />
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
}
